import {
  Users,
  Wallet,
  Target,
  DollarSign,
  ShieldCheck,
  CircleAlert,
  Calendar,
  Download,
  TrendingUp,
  TrendingDown,
  FileText,
  Landmark,
  Building2,
  ChevronRight,
  ChartColumn,
  ChartPie,
  Activity,
  Bell,
  Clock,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart as RechartsBarChart,
  Bar,
} from "recharts";

const StatCard = ({ icon, title, value, change, positive, danger }: any) => (
  <div className="relative rounded-2xl border bg-card p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
    <div className="mb-3 flex items-start justify-between">
      <div className="rounded-xl bg-primary/10 p-2.5 text-primary">{icon}</div>
      <span
        className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
          danger
            ? "bg-red-50 text-red-500"
            : positive
              ? "bg-green-50 text-green-600"
              : "bg-muted text-muted-foreground"
        }`}
      >
        {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {change}
      </span>
    </div>

    <p className="text-2xl font-bold text-secondary">{value}</p>
    <p className="mt-1 text-xs font-medium text-muted-foreground">{title}</p>
  </div>
);

const revenueData = [
  { month: "Jul", bonds: 2500, fds: 3200, ipo: 1800 },
  { month: "Aug", bonds: 3500, fds: 4100, ipo: 2400 },
  { month: "Sep", bonds: 5000, fds: 5500, ipo: 3200 },
  { month: "Oct", bonds: 6500, fds: 6800, ipo: 4500 },
  { month: "Nov", bonds: 8000, fds: 8500, ipo: 5800 },
  { month: "Dec", bonds: 9500, fds: 10200, ipo: 7200 },
];

const aumData = [
  { name: "Bonds", value: 35, color: "#1D2C56" },
  { name: "IPO", value: 25, color: "#457297" },
  { name: "Fixed Deposits", value: 30, color: "#66A0CD" },
  { name: "NPS", value: 10, color: "#9FC6E3" },
];

const dailyActivity = [
  { day: "Mon", logins: 3200, transactions: 145 },
  { day: "Tue", logins: 4100, transactions: 189 },
  { day: "Wed", logins: 3500, transactions: 156 },
  { day: "Thu", logins: 5200, transactions: 234 },
  { day: "Fri", logins: 4800, transactions: 212 },
  { day: "Sat", logins: 2800, transactions: 98 },
  { day: "Sun", logins: 1800, transactions: 67 },
];

const QuickLink = ({ icon, label, href, colorClass }: any) => (
  <a href={href} className="group">
    <div className="flex items-center gap-3 rounded-2xl border bg-card p-4 transition hover:border-[#1D2C56]/30 hover:shadow-md">
      <div className={`rounded-xl p-3 ${colorClass}`}>{icon}</div>
      <p className="flex-1 text-sm font-semibold text-secondary">{label}</p>
      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[#1D2C56]" />
    </div>
  </a>
);

const Card = ({ title, icon, children, action, subtitle, className }: any) => (
  <div
    className={`rounded-2xl border bg-card shadow-sm transition hover:shadow-md ${className}`}
  >
    <div className="flex items-start justify-between p-6 pb-2">
      <div className="flex items-start gap-3">
        <div className="mt-1 rounded-lg bg-slate-100 p-2 text-slate-600">
          {icon}
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          {subtitle && (
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          )}
        </div>
      </div>
      {action && (
        <button className="mt-1 text-slate-400 hover:text-slate-600">
          {action}
        </button>
      )}
    </div>
    <div className="p-6 pt-0">{children}</div>
  </div>
);

// const ProductRow = ({ name, value }: any) => (
//   <li className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
//     <span className="font-semibold">{name}</span>
//     <span className="font-bold">{value}</span>
//   </li>
// );

const Th = ({ children, right }: any) => (
  <th
    className={`p-4 text-left text-sm font-medium text-secondary ${
      right ? "text-right" : ""
    }`}
  >
    {children}
  </th>
);

const ActivityRow = ({ activity, user, product, amount, status }: any) => (
  <tr className="border-b transition hover:bg-muted/20">
    <td className="p-4 font-medium">{activity}</td>
    <td className="p-4 text-muted-foreground">{user}</td>
    <td className="p-4">{product}</td>
    <td className="p-4 text-right font-semibold">{amount}</td>
    <td className="p-4 capitalize">{status}</td>
    <td className="flex items-center gap-1 p-4 text-muted-foreground">
      <Clock className="h-3 w-3" /> just now
    </td>
  </tr>
);

const products = [
  {
    rank: 1,
    name: "REC Limited Bond",
    meta: "Bond • 234 investors",
    amount: "₹23.4 Cr",
    growth: "+12%",
  },
  {
    rank: 2,
    name: "HDFC 1-Year FD",
    meta: "FD • 189 investors",
    amount: "₹18.9 Cr",
    growth: "+8%",
  },
  {
    rank: 3,
    name: "Swiggy IPO",
    meta: "IPO • 156 investors",
    amount: "₹15.6 Cr",
    growth: "+45%",
  },
  {
    rank: 4,
    name: "ICICI Prudential NPS",
    meta: "NPS • 98 investors",
    amount: "₹4.9 Cr",
    growth: "+15%",
  },
];

const DashboardPage = () => {
  return (
    <main className="flex-1 overflow-y-auto p-8">
      <div className="space-y-6">
        {/* ================= HERO ================= */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/30 p-6 md:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(168_72%_40%/0.2)_0%,transparent_60%)]" />
          <div className="absolute bottom-0 left-0 h-64 w-64 -translate-x-1/2 translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="text-white">
              <p className="mb-1 text-sm font-medium text-white/60">
                Admin Dashboard
              </p>
              <h1 className="text-2xl font-bold md:text-3xl">
                Welcome back, Admin
              </h1>
              <p className="mt-1 text-white/70">
                Here&apos;s what&apos;s happening with your platform today
              </p>
            </div>

            <div className="flex gap-3">
              <button className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/20">
                <Calendar className="h-4 w-4" />
                Last 30 Days
              </button>

              <button className="flex items-center gap-2 rounded-xl bg-white px-5 py-2 text-sm font-semibold text-secondary shadow-sm transition hover:bg-white/90">
                <Download className="h-4 w-4" />
                Export Report
              </button>
            </div>
          </div>
        </div>

        {/* ================= KPI CARDS ================= */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <StatCard
            icon={<Users />}
            title="Total Users"
            value="12,845"
            change="+12.5%"
            positive
          />
          <StatCard
            icon={<Wallet />}
            title="Total AUM"
            value="₹48.5 Cr"
            change="+23.1%"
            positive
          />
          <StatCard
            icon={<Target />}
            title="Active Investments"
            value="8,234"
            change="+8.2%"
            positive
          />
          <StatCard
            icon={<DollarSign />}
            title="Revenue MTD"
            value="₹12.4 L"
            change="+18.6%"
            positive
          />
          <StatCard
            icon={<ShieldCheck />}
            title="KYC Pending"
            value="156"
            change="-24%"
          />
          <StatCard
            icon={<CircleAlert />}
            title="Support Tickets"
            value="23"
            change="+5"
            danger
          />
        </div>

        {/* ================= QUICK ACTIONS ================= */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <QuickLink
            icon={<FileText />}
            label="Add New IPO"
            href="/admin/ipo-management"
            colorClass="bg-[#66A0CD]/20 text-[#1D2C56]"
          />
          <QuickLink
            icon={<Landmark />}
            label="Manage Bonds"
            href="/admin/bonds"
            colorClass="bg-[#1D2C56]/10 text-[#1D2C56]"
          />
          <QuickLink
            icon={<Building2 />}
            label="FD Providers"
            href="/admin/fixed-deposits"
            colorClass="bg-[#66A0CD]/20 text-[#1D2C56]"
          />
          <QuickLink
            icon={<Users />}
            label="User Management"
            href="/admin/user-management"
            colorClass="bg-[#1D2C56]/10 text-[#1D2C56]"
          />
        </div>

        {/* ================= PLACEHOLDERS FOR CHARTS ================= */}
        <div className="flex w-full flex-col gap-6 lg:flex-row">
          <Card
            title="Revenue by Product"
            subtitle="Monthly commission breakdown (in ₹ thousands)"
            icon={<ChartColumn className="h-4 w-4" />}
            action={<Download className="h-4 w-4" />}
            className="w-2/3"
            style={{
              backgroundColor: "red",
            }}
          >
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorBonds" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1D2C56" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1D2C56" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorFDs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#66A0CD" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#66A0CD" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorIPO" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8AB7D9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8AB7D9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    domain={[0, 12000]}
                    ticks={[0, 3000, 6000, 9000, 12000]}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value: number | undefined) => [
                      `₹${value?.toLocaleString()}K`,
                      "",
                    ]}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: "20px" }}
                    iconType="circle"
                    align="center"
                  />
                  <Area
                    type="monotone"
                    dataKey="ipo"
                    name="IPO"
                    stackId="1"
                    stroke="#8AB7D9"
                    fillOpacity={1}
                    fill="url(#colorIPO)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="fds"
                    name="FDs"
                    stackId="1"
                    stroke="#66A0CD"
                    fillOpacity={1}
                    fill="url(#colorFDs)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="bonds"
                    name="Bonds"
                    stackId="1"
                    stroke="#1D2C56"
                    fillOpacity={1}
                    fill="url(#colorBonds)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card
            title="AUM Distribution"
            subtitle="By product category"
            icon={<ChartPie className="h-4 w-4" />}
            className="w-2/6"
          >
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={aumData}
                    cx="50%"
                    cy="40%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {aumData.map(entry => (
                      <Cell key={`cell-${entry.name}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value: number | undefined) => [
                      `${value?.toLocaleString()}%`,
                      "",
                    ]}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: "20px" }}
                    iconType="circle"
                    align="center"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card
            title="Weekly Activity"
            subtitle="User logins & transactions"
            icon={<Activity />}
          >
            <div className="mt-8 h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart data={dailyActivity}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="logins"
                    name="Logins"
                    fill="#66A0CD"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="transactions"
                    name="Transactions"
                    fill="#1D2C56"
                    radius={[4, 4, 0, 0]}
                  />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="rounded-2xl border border-border/60 bg-card text-card-foreground shadow-sm transition-shadow duration-300 hover:shadow-md">
            {/* ===== Header ===== */}
            <div className="flex flex-col space-y-1.5 p-6 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold tracking-tight text-foreground">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Top Performing Products
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    By investment volume this month
                  </p>
                </div>

                <button className="h-9 rounded-lg px-4 text-xs font-semibold text-foreground transition hover:bg-muted/50">
                  View All
                </button>
              </div>
            </div>

            {/* ===== List ===== */}
            <div className="p-6 pt-0">
              <div className="space-y-4">
                {products.map(product => (
                  <div
                    key={product.rank}
                    className="flex items-center gap-4 rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50"
                  >
                    {/* Rank */}
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
                      {product.rank}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-secondary">
                        {product.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {product.meta}
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="text-right">
                      <p className="font-bold text-secondary">
                        {product.amount}
                      </p>
                      <p className="text-xs text-green-600">{product.growth}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ================= RECENT ACTIVITIES ================= */}
        <Card title="Recent Activities" icon={<Bell />} action="View All">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <Th>Activity</Th>
                <Th>User</Th>
                <Th>Product</Th>
                <Th right>Amount</Th>
                <Th>Status</Th>
                <Th>Time</Th>
              </tr>
            </thead>
            <tbody>
              <ActivityRow
                activity="New IPO Application"
                user="Rahul Sharma"
                product="Swiggy IPO"
                amount="₹1,50,000"
                status="pending"
              />
              <ActivityRow
                activity="Bond Purchase"
                user="Priya Patel"
                product="REC Ltd Bond"
                amount="₹5,00,000"
                status="completed"
              />
            </tbody>
          </table>
        </Card>
      </div>
    </main>
  );
};

export default DashboardPage;
